import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Chip,
    IconButton,
    Collapse,
    useTheme,
    alpha,
    Skeleton
} from '@mui/material';
import {
    Lightbulb,
    ExpandMore,
    ExpandLess,
    Refresh
} from '@mui/icons-material';
import { getRandomTip, getRandomTipByCategory } from '../../api/tips';

const TipDisplay = ({ categoryId = null, compact = false, showRefresh = true }) => {
    const theme = useTheme();
    const [tip, setTip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(!compact);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTip();
    }, [categoryId]);

    const fetchTip = async () => {
        setLoading(true);
        setError('');
        try {
            const response = categoryId
                ? await getRandomTipByCategory(categoryId)
                : await getRandomTip();
            setTip(response.data);
        } catch (err) {
            setError('Unable to load tip');
            console.error('Error fetching tip:', err);
        } finally {
            setLoading(false);
        }
    };

    const getCategoryColor = (categoryName) => {
        const colors = {
            'Organic': '#4caf50',
            'Recyclable': '#2196f3',
            'Hazardous': '#f44336',
            'General': '#9e9e9e',
            'E-Waste': '#ff9800'
        };
        return colors[categoryName] || theme.palette.primary.main;
    };

    if (loading) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={120} height={20} />
                    </Box>
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                </CardContent>
            </Card>
        );
    }

    if (error || !tip) {
        return null;
    }

    return (
        <Card
            sx={{
                mb: { xs: 1, sm: 2 },
                mx: { xs: 1, sm: 0 },
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: theme.shadows[4]
                }
            }}
        >
            <CardContent sx={{
                pb: compact && !expanded ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
                px: { xs: 2, sm: 3 }
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: { xs: 1.5, sm: 2 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 1, sm: 0 }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Lightbulb sx={{
                            color: theme.palette.secondary.main,
                            mr: 1,
                            fontSize: { xs: 18, sm: 20 }
                        }} />
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            💡 {compact ? 'Eco Tip' : 'Educational Tip'}
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 'fit-content'
                    }}>
                        {showRefresh && (
                            <IconButton
                                size="small"
                                onClick={fetchTip}
                                sx={{
                                    color: theme.palette.primary.main,
                                    padding: { xs: 0.5, sm: 1 }
                                }}
                            >
                                <Refresh fontSize="small" />
                            </IconButton>
                        )}
                        {compact && (
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    padding: { xs: 0.5, sm: 1 }
                                }}
                            >
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Content */}
                <Collapse in={expanded} timeout="auto">
                    <Box>
                        {tip.title && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: { xs: 0.75, sm: 1 },
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                                    lineHeight: { xs: 1.3, sm: 1.4 }
                                }}
                            >
                                {tip.title}
                            </Typography>
                        )}

                        {tip.imageUrl && (
                            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                                <CardMedia
                                    component="img"
                                    image={tip.imageUrl}
                                    alt={tip.title || 'Educational tip'}
                                    sx={{
                                        height: {
                                            xs: compact ? 100 : 140,
                                            sm: compact ? 120 : 160,
                                            md: compact ? 140 : 180
                                        },
                                        borderRadius: 2,
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        )}

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mb: { xs: 1.5, sm: 2 },
                                lineHeight: { xs: 1.5, sm: 1.6 },
                                fontSize: {
                                    xs: compact ? '0.8rem' : '0.875rem',
                                    sm: compact ? '0.875rem' : '1rem',
                                    md: compact ? '0.875rem' : '1.1rem'
                                }
                            }}
                        >
                            {tip.tipText}
                        </Typography>

                        {tip.category && (
                            <Chip
                                label={tip.category.name}
                                size="small"
                                sx={{
                                    background: alpha(getCategoryColor(tip.category.name), 0.1),
                                    color: getCategoryColor(tip.category.name),
                                    fontWeight: 600,
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    height: { xs: 24, sm: 28 }
                                }}
                            />
                        )}
                    </Box>
                </Collapse>

                {/* Collapsed preview */}
                {compact && !expanded && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            lineHeight: { xs: 1.3, sm: 1.4 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 3, sm: 2 },
                            WebkitBoxOrient: 'vertical',
                            cursor: 'pointer',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}
                        onClick={() => setExpanded(true)}
                    >
                        {tip.tipText}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default TipDisplay;
